/** One rod of a standard soroban. Earth beads against the beam are a prefix, with no gaps. */
export interface SorobanColumnState {
  upperActive: boolean;
  lowerActive: number;
}

/** Plain bead state a later session can copy. Column 0 is the highest place. */
export interface VirtualAbacusState {
  columns: SorobanColumnState[];
  value: number;
}

export const HINT_NUMBER = 5;
export const PRACTICE_NUMBER = 47;
export const VALIDATOR_FIXTURES = [0, 5, 9, 10, 47, 99] as const;
export const BEAD_PX = 48;

export function cleared(columnCount: number): SorobanColumnState[] {
  return Array.from({ length: columnCount }, () => ({ upperActive: false, lowerActive: 0 }));
}

export function isLegalColumn(column: SorobanColumnState): boolean {
  return (
    typeof column.upperActive === 'boolean' &&
    Number.isInteger(column.lowerActive) &&
    column.lowerActive >= 0 &&
    column.lowerActive <= 4
  );
}

export function digitOf(column: SorobanColumnState): number {
  if (!isLegalColumn(column)) {
    throw new Error('Illegal bead column');
  }
  return (column.upperActive ? 5 : 0) + column.lowerActive;
}

/** Integer shown by the rods. Column 0 is the highest place. */
export function valueOf(columns: SorobanColumnState[]): number {
  return columns.reduce((sum, column, index) => {
    const place = 10 ** (columns.length - index - 1);
    return sum + digitOf(column) * place;
  }, 0);
}

/**
 * Places a number on the rods. Rejects negatives and numbers that need more rods.
 * Heaven is against the beam when the digit is 5 or more. Earth count is the remainder.
 */
export function encodeNumber(value: number, columnCount: number): SorobanColumnState[] {
  if (!Number.isInteger(value) || value < 0 || columnCount < 1 || value >= 10 ** columnCount) {
    throw new Error('Number does not fit these rods');
  }
  return String(value)
    .padStart(columnCount, '0')
    .split('')
    .map((digit) => {
      const n = Number(digit);
      return { upperActive: n >= 5, lowerActive: n % 5 };
    });
}

export function matchesPrompt(columns: SorobanColumnState[], prompt: number): boolean {
  return columns.every(isLegalColumn) && valueOf(columns) === prompt;
}

export function snapshot(columns: SorobanColumnState[]): VirtualAbacusState {
  return {
    columns: columns.map((column) => ({ upperActive: column.upperActive, lowerActive: column.lowerActive })),
    value: valueOf(columns),
  };
}

/** Heaven bead toggles between the top of the rod and the beam. It cannot cross the beam. */
export function toggleHeaven(columns: SorobanColumnState[], columnIndex: number): SorobanColumnState[] {
  return columns.map((column, index) =>
    index === columnIndex ? { ...column, upperActive: !column.upperActive } : column
  );
}

/**
 * Earth bead {@code beadIndex} (0 is closest to the beam) moves with every bead it would
 * otherwise pass through. A bead away from the beam pulls the beads between it and the beam up.
 * A bead against the beam pushes itself and the beads farther from the beam down.
 */
export function moveEarth(
  columns: SorobanColumnState[],
  columnIndex: number,
  beadIndex: number
): SorobanColumnState[] {
  if (!Number.isInteger(beadIndex) || beadIndex < 0 || beadIndex > 3) {
    throw new Error('Earth bead cannot leave the rod');
  }
  return columns.map((column, index) => {
    if (index !== columnIndex) {
      return column;
    }
    const lowerActive = beadIndex < column.lowerActive ? beadIndex : beadIndex + 1;
    return { ...column, lowerActive };
  });
}

export function placeLabel(place: number): string {
  if (place === 0) return 'ones';
  if (place === 1) return 'tens';
  if (place === 2) return 'hundreds';
  return `${10 ** place}s`;
}

/** True when a 2D canvas context can be created. The widget does not draw with WebGL. */
export function canvas2dAvailable(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('2d'));
  } catch {
    return false;
  }
}
